import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import React from 'react';
import { fmtCurrency } from './contactOverviewFormatUtils';
import { OverviewMetrics } from './contactOverviewTypes';

export default function ContactOverviewHeroCard({
  metrics,
  loading,
  committedInvestment,
  completeFunds,
  includeClosedFunds,
  onToggleClosedFunds,
}: {
  metrics: OverviewMetrics;
  loading: boolean;
  committedInvestment: number | null;
  completeFunds: number;
  includeClosedFunds: boolean;
  onToggleClosedFunds: (v: boolean) => void;
}) {
  const totalDisplay = committedInvestment != null ? fmtCurrency(committedInvestment) : '—';

  return (
    <Paper
      elevation={2}
      sx={{
        borderRadius: 3,
        background: 'linear-gradient(135deg, #1a3a2a 0%, #2e5c42 100%)',
        color: '#fff',
        p: 3,
        mb: 3,
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: '#d8eadf', letterSpacing: 1.5, display: 'block', mb: 0.5 }}
      >
        Investment Overview
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 0.2, mb: 1.5 }}>
        {totalDisplay}
      </Typography>

      {loading ? (
        <Box sx={{ height: 64, display: 'flex', alignItems: 'center' }}>
          <CircularProgress size={22} sx={{ color: 'rgba(255,255,255,0.7)' }} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {[
            {
              label: 'ROE',
              value:
                metrics.projectedRoePercent != null
                  ? `${metrics.projectedRoePercent.toFixed(0)}%`
                  : '—',
            },
            {
              label: 'IRR',
              value:
                metrics.projectedIrrPercent != null
                  ? `${metrics.projectedIrrPercent.toFixed(1)}%`
                  : '—',
            },
            {
              label: 'Money Multiple',
              value: metrics.moneyMultiple != null ? `${metrics.moneyMultiple.toFixed(2)}×` : '—',
            },
          ].map(({ label, value }) => (
            <Box
              key={label}
              sx={{
                flexGrow: 1,
                flexBasis: '28%',
                minWidth: 96,
                bgcolor: 'rgba(255,255,255,0.12)',
                borderRadius: 2,
                py: 1.25,
                px: 1.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#d8eadf',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  display: 'block',
                  mb: 0.5,
                  minHeight: 32,
                }}
              >
                {label}
              </Typography>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {completeFunds > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 2,
            pt: 1.5,
            borderTop: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <Typography variant="caption" sx={{ color: '#d8eadf' }}>
            Include {completeFunds} closed fund{completeFunds !== 1 ? 's' : ''}
          </Typography>
          <Box
            component="input"
            type="checkbox"
            checked={includeClosedFunds}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onToggleClosedFunds(e.target.checked)
            }
            sx={{ accentColor: '#fff', width: 18, height: 18, cursor: 'pointer' }}
          />
        </Box>
      )}
    </Paper>
  );
}
